const DIGITS = [1, 2, 3, 4, 5, 6, 7, 8, 9];

const cloneGrid = (grid) => grid.map((row) => row.map((cell) => cell ?? null));

const uniqueConflicts = (cells) => {
  const seen = new Set();
  return cells.filter((cell) => {
    const key = `${cell.row}:${cell.col}`;
    if (seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
};

const findDuplicates = (entries) => {
  const grouped = new Map();

  entries.forEach((entry) => {
    if (!entry.value) {
      return;
    }

    if (!grouped.has(entry.value)) {
      grouped.set(entry.value, []);
    }

    grouped.get(entry.value).push({ row: entry.row, col: entry.col });
  });

  return Array.from(grouped.values())
    .filter((cells) => cells.length > 1)
    .flat();
};

export function normalizeSudokuGrid(grid = []) {
  return Array.from({ length: 9 }, (_, rowIndex) =>
    Array.from({ length: 9 }, (_, colIndex) => {
      const value = grid?.[rowIndex]?.[colIndex];
      return Number.isInteger(value) && value >= 1 && value <= 9 ? value : null;
    })
  );
}

export function getGivenMask(grid = []) {
  return normalizeSudokuGrid(grid).map((row) => row.map((cell) => cell !== null));
}

export function isComplete(grid) {
  return normalizeSudokuGrid(grid).flat().every((cell) => cell !== null);
}

export function getConflictCells(grid) {
  const normalized = normalizeSudokuGrid(grid);
  const conflicts = [];

  normalized.forEach((row, rowIndex) => {
    conflicts.push(
      ...findDuplicates(
        row.map((value, colIndex) => ({ row: rowIndex, col: colIndex, value }))
      )
    );
  });

  for (let colIndex = 0; colIndex < 9; colIndex += 1) {
    conflicts.push(
      ...findDuplicates(
        normalized.map((row, rowIndex) => ({
          row: rowIndex,
          col: colIndex,
          value: row[colIndex],
        }))
      )
    );
  }

  for (let boxRow = 0; boxRow < 3; boxRow += 1) {
    for (let boxCol = 0; boxCol < 3; boxCol += 1) {
      const entries = [];

      for (let row = boxRow * 3; row < boxRow * 3 + 3; row += 1) {
        for (let col = boxCol * 3; col < boxCol * 3 + 3; col += 1) {
          entries.push({ row, col, value: normalized[row][col] });
        }
      }

      conflicts.push(...findDuplicates(entries));
    }
  }

  return uniqueConflicts(conflicts);
}

export function getCandidates(grid, row, col) {
  const normalized = normalizeSudokuGrid(grid);

  if (normalized[row][col] !== null) {
    return [];
  }

  const usedValues = new Set();

  normalized[row].forEach((value) => value && usedValues.add(value));
  normalized.forEach((rowValues) => rowValues[col] && usedValues.add(rowValues[col]));

  const boxRowStart = Math.floor(row / 3) * 3;
  const boxColStart = Math.floor(col / 3) * 3;

  for (let boxRow = boxRowStart; boxRow < boxRowStart + 3; boxRow += 1) {
    for (let boxCol = boxColStart; boxCol < boxColStart + 3; boxCol += 1) {
      const value = normalized[boxRow][boxCol];
      if (value) {
        usedValues.add(value);
      }
    }
  }

  return DIGITS.filter((digit) => !usedValues.has(digit));
}

const findSingleCandidateHint = (grid) => {
  const normalized = normalizeSudokuGrid(grid);

  for (let row = 0; row < 9; row += 1) {
    for (let col = 0; col < 9; col += 1) {
      const candidates = getCandidates(normalized, row, col);
      if (candidates.length === 1) {
        return {
          technique: 'single-candidate',
          row,
          col,
          value: candidates[0],
          message: `Row ${row + 1}, column ${col + 1} can only be ${candidates[0]}.`,
        };
      }
    }
  }

  return null;
};

const hiddenSingleInUnit = (grid, cells, label) => {
  const candidateMap = new Map();

  cells.forEach(({ row, col }) => {
    const candidates = getCandidates(grid, row, col);
    candidates.forEach((candidate) => {
      if (!candidateMap.has(candidate)) {
        candidateMap.set(candidate, []);
      }

      candidateMap.get(candidate).push({ row, col });
    });
  });

  for (const [candidate, positions] of candidateMap.entries()) {
    if (positions.length === 1) {
      const [position] = positions;
      return {
        technique: 'hidden-single',
        row: position.row,
        col: position.col,
        value: candidate,
        message: `${candidate} only fits once in ${label}.`,
      };
    }
  }

  return null;
};

export function getHint(grid) {
  const normalized = normalizeSudokuGrid(grid);
  const conflicts = getConflictCells(normalized);

  if (conflicts.length) {
    return {
      status: 'invalid',
      message: 'Clear the highlighted conflicts before asking for a hint.',
      highlights: {
        invalidCells: conflicts,
      },
    };
  }

  const directHint = findSingleCandidateHint(normalized);
  if (directHint) {
    return {
      status: 'ok',
      nextStep: directHint,
      explanation: directHint.message,
      highlights: {
        targetCells: [{ row: directHint.row, col: directHint.col }],
      },
    };
  }

  for (let row = 0; row < 9; row += 1) {
    const rowHint = hiddenSingleInUnit(
      normalized,
      Array.from({ length: 9 }, (_, col) => ({ row, col })),
      `row ${row + 1}`
    );
    if (rowHint) {
      return {
        status: 'ok',
        nextStep: rowHint,
        explanation: rowHint.message,
        highlights: {
          targetCells: [{ row: rowHint.row, col: rowHint.col }],
        },
      };
    }
  }

  for (let col = 0; col < 9; col += 1) {
    const colHint = hiddenSingleInUnit(
      normalized,
      Array.from({ length: 9 }, (_, row) => ({ row, col })),
      `column ${col + 1}`
    );
    if (colHint) {
      return {
        status: 'ok',
        nextStep: colHint,
        explanation: colHint.message,
        highlights: {
          targetCells: [{ row: colHint.row, col: colHint.col }],
        },
      };
    }
  }

  for (let boxRow = 0; boxRow < 3; boxRow += 1) {
    for (let boxCol = 0; boxCol < 3; boxCol += 1) {
      const cells = [];

      for (let row = boxRow * 3; row < boxRow * 3 + 3; row += 1) {
        for (let col = boxCol * 3; col < boxCol * 3 + 3; col += 1) {
          cells.push({ row, col });
        }
      }

      const boxHint = hiddenSingleInUnit(
        normalized,
        cells,
        `box ${boxRow * 3 + boxCol + 1}`
      );

      if (boxHint) {
        return {
          status: 'ok',
          nextStep: boxHint,
          explanation: boxHint.message,
          highlights: {
            targetCells: [{ row: boxHint.row, col: boxHint.col }],
          },
        };
      }
    }
  }

  return {
    status: 'stalled',
    explanation:
      'No simple single-candidate hint is available from the current state yet.',
    highlights: {
      targetCells: [],
    },
  };
}

export function validateSudoku(grid) {
  const normalized = cloneGrid(normalizeSudokuGrid(grid));
  const conflicts = getConflictCells(normalized);

  if (conflicts.length) {
    return {
      valid: false,
      complete: false,
      conflicts,
      message: 'There are duplicate digits in a row, column, or 3x3 box.',
    };
  }

  if (isComplete(normalized)) {
    return {
      valid: true,
      complete: true,
      conflicts: [],
      message: 'Puzzle solved cleanly. Nice work.',
    };
  }

  return {
    valid: true,
    complete: false,
    conflicts: [],
    message: 'So far so good. Keep going.',
  };
}
